import React, { useState } from 'react';
import { Dropdown, DropdownAnchor, DropdownContent, DropdownMenu, MenuGroup, MenuItem } from '@fiverr-private/dropdown';
import { IconButton } from '@fiverr-private/button';
import { EllipsisIcon } from '@fiverr-private/visuals';
import { I18n } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { Stack } from '@fiverr-private/layout_components';
import { Loader } from '@fiverr-private/feedback';
import { DocumentStatus, SentTemplateData, TemplateResponseObject } from '../../api';
import { RemoveDocumentConfirmModal } from '../ConfirmModal/RemoveDocument/RemoveDocument';
import { LegalDocsConfigurations } from '../../types';
import { useDocumentActions } from './useDocumentActions';

const TRANSLATION_PREFIX = 'legal_documents_package.seller_templates.actions';

export interface DocumentActionsProps {
    document: SentTemplateData;
    template?: TemplateResponseObject;
    configurations?: LegalDocsConfigurations;
}

export const DocumentActions = ({ document, template, configurations }: DocumentActionsProps) => {
    const { isLoadingPreviewUrl, openPreviewUrl, resendDocument, removeDocument } = useDocumentActions({ document });

    const [isDeleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
    const openDeleteConfirmModal = () => setDeleteConfirmModalOpen(true);
    const closeDeleteConfirmModal = () => setDeleteConfirmModalOpen(false);

    const isAllowedToView = document.currentStatus === DocumentStatus.SIGNED;
    const shouldShowResend = document.currentStatus !== DocumentStatus.SIGNED;
    const isAllowedToResend = !!template;

    const isPremiumPackage = !!configurations?.legalDocumentsPackageData?.isFree;
    const isAllowedToRemove = ![DocumentStatus.SIGNED, DocumentStatus.EXPIRED].includes(document.currentStatus);

    return (
        <>
            <Dropdown position="bottom-end" defaultValues={{ item: '' } /* removes auto selection of actions*/}>
                <DropdownAnchor embed>
                    <IconButton aria-label={'actions'} shape="circle" size="xs" variant="ghost">
                        <EllipsisIcon />
                    </IconButton>
                </DropdownAnchor>
                <DropdownContent>
                    <DropdownMenu>
                        <MenuGroup hideSelectedIndicator>
                            {isAllowedToView && (
                                <MenuItem onClick={openPreviewUrl} disabled={isLoadingPreviewUrl}>
                                    <Stack>
                                        <Text>
                                            <Stack>
                                                <I18n k={`${TRANSLATION_PREFIX}.view`} />{' '}
                                                {isLoadingPreviewUrl && <Loader size="sm" />}
                                            </Stack>
                                        </Text>
                                    </Stack>
                                </MenuItem>
                            )}
                            {isAllowedToRemove && (
                                <MenuItem onClick={openDeleteConfirmModal}>
                                    <I18n k={`${TRANSLATION_PREFIX}.remove`} />
                                </MenuItem>
                            )}
                            {shouldShowResend && (
                                <MenuItem disabled={!isAllowedToResend} onClick={resendDocument}>
                                    <I18n k={`${TRANSLATION_PREFIX}.resend`} />
                                </MenuItem>
                            )}
                        </MenuGroup>
                    </DropdownMenu>
                </DropdownContent>
            </Dropdown>
            <RemoveDocumentConfirmModal
                isOpen={isDeleteConfirmModalOpen}
                onRemove={removeDocument}
                onClose={closeDeleteConfirmModal}
                isPremiumPackage={isPremiumPackage}
            />
        </>
    );
};
